import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import DynamicRoutingDialog from './index';
import { autorun } from 'mobx';
import { isEqual } from 'lodash';
import { withFeatureFlags } from '@withFeatureFlags';
import appConstants from '@appConstants';

@inject("IBPlanningStore", "CommonStore")
@observer
class DynamicRoutingManager extends Component {
  constructor(props) {
    super(props);
    const initialCityRoutes = this.getCityRoutesForDynamicRouting();
    this.isDynamicRoutingTriggeredByShipmentSelection = initialCityRoutes?.length > 0;
    this.state = {
      cityRoutes: initialCityRoutes,
      isLoadingCityRoutes: true,
    };
  }

  componentDidMount() {
    if (!this.isDynamicRoutingTriggeredByShipmentSelection) {
      return;
    }
    // MobX autorun: reacts to IBPlanningStore (selectedRouteNameList, routesList) so city routes stay in sync and loader hides when routes load; see docs/features/PENDING_ENFORCED_DYNAMIC_ROUTING.md
    this.autorunDisposer = autorun(() => {
        if (!this.props.show) return;
        const currentCityRoutes = this.getCityRoutesForDynamicRouting();
        if (currentCityRoutes.length === 0) {
          this.setState({ cityRoutes: [], isLoadingCityRoutes: false });
          return;
        }
        this.setState({ cityRoutes: currentCityRoutes });
        const selectedRoutesList = this.props.IBPlanningStore.selectedRouteNameList;
        const routesMatched = isEqual(selectedRoutesList, currentCityRoutes);
        const cityRoutesNotFound = !this.isCityRoutesPresentInRoutesList(currentCityRoutes);
        const routesListLoaded = this.props.IBPlanningStore.routesList?.length > 0;
        const routesReady = routesMatched || (cityRoutesNotFound && routesListLoaded);
        if (this.canShowDialogContent(routesReady)) {
          this.setState({ isLoadingCityRoutes: false });
        }
    });
  }

  componentWillUnmount() {
    if (this.isDynamicRoutingTriggeredByShipmentSelection && this.autorunDisposer) {
      this.autorunDisposer();
    }
  }

  /** True when routes are ready and parameters/waves APIs have responded (success, empty, or error). Used to show Dynamic Routing dialog content instead of loading state. */
  canShowDialogContent = (routesReady) => {
    const parametersLoaded = this.props.CommonStore?.parametersDataLoaded;
    const wavesLoaded = this.props.CommonStore?.wavesDataLoaded;
    const dependentDataLoaded = parametersLoaded && wavesLoaded;
    return routesReady && dependentDataLoaded;
  };

  isCityRoutesPresentInRoutesList = (cityRoutes = this.state.cityRoutes) => {
    const { routesList } = this.props.IBPlanningStore;
    if (!routesList || routesList.length === 0 || !cityRoutes?.length) {
      return !cityRoutes?.length;
    }

    const availableRoutes = routesList.map(route => route.routeName);
    const allRoutesExist = cityRoutes.every(
      routeName => availableRoutes.includes(routeName)
    );

    return allRoutesExist;
  };

  handleClose = () => {
    this.props.onClose?.();
  };

  getCityRoutesForDynamicRouting = () => {
    const isEnforcedDREnabled = this.props.featureFlags?.isFeatureEnabled?.(
      appConstants.features.EnableEnforcedDynamicRoutingFromShipmentSelection
    );
    if (!isEnforcedDREnabled) return [];
    if (!this.props.IBPlanningStore.isFirstOptimizationRun) return [];
    return this.props.IBPlanningStore.cityRouteNamesForEnforcedDR;
  };

  render() {
    if (!this.props.show) {
      return null;
    }

    return (
      <DynamicRoutingDialog
        {...this.props}
        onClose={this.handleClose}
        isCityRoutesPresentInRoutesList={this.isCityRoutesPresentInRoutesList}
        isDynamicRoutingTriggeredByShipmentSelection={this.isDynamicRoutingTriggeredByShipmentSelection}
        isDataLoading={this.isDynamicRoutingTriggeredByShipmentSelection && this.state.isLoadingCityRoutes}
        cityRoutes={this.state.cityRoutes}
        key={this.isDynamicRoutingTriggeredByShipmentSelection && this.state.isLoadingCityRoutes ? 'loading' : 'ready'}
      />
    );
  }
}

export default withFeatureFlags(DynamicRoutingManager);
